export class ApiError extends Error {
  constructor(message: string, public status: number, public body?: unknown) {
    super(message);
  }
}

type HttpOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  headers?: Record<string, string>;
  query?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
  signal?: AbortSignal;
};

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const BASE_PATH_API = process.env.REACT_APP_API_BASE_PATH || "";

function buildUrl(path: string, query?: HttpOptions["query"]) {
  const url = new URL(`${BASE_PATH_API}${path}`, BASE_URL);
  if (query) {
    Object.entries(query).forEach(([k, v]) => {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    });
  }
  return url.toString();
}

export async function http<T>(path: string, opts: HttpOptions = {}): Promise<T> {
  const { method = "GET", headers, body, query, signal } = opts;

  const isFile = body instanceof File || body instanceof Blob;

  const token = localStorage.getItem("access_token"); // ou autre store
  const h: Record<string, string> = {
    "Content-Type": isFile ? body?.type ?? "application/octet-stream" : "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...headers,
  };

  const res = await fetch(buildUrl(path, query), {
    method,
    headers: h,
    body: body !== undefined ? (isFile ? body : JSON.stringify(body)) : undefined,
    signal,
    credentials: "include",
  });

  let data: unknown = null;
  const isJson = res.headers.get("content-type")?.includes("application/json");
  try {
    data = isJson ? await res.json() : await res.text();
  } catch {}

  if (!res.ok) {
    throw new ApiError(`HTTP ${res.status} on ${path}`, res.status, data);
  }
  return data as T;
}

export type BlobResponse = {
  blob: Blob;
  filename?: string;
};

function parseContentDispositionFilename(header: string | null): string | undefined {
  if (!header) return undefined;

  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);

  const match = header.match(/filename="([^"]+)"/i);
  return match?.[1];
}

export async function postBlob(
  path: string,
  body?: unknown,
  opts: Omit<HttpOptions, "method" | "body"> = {}
): Promise<BlobResponse> {
  const { headers, query, signal } = opts;

  const h: Record<string, string> = {
    "Content-Type": "application/json",
    ...headers,
  };

  const res = await fetch(buildUrl(path, query), {
    method: "POST",
    headers: h,
    body: JSON.stringify(body ?? {}),
    signal,
    credentials: "include",
  });

  if (!res.ok) {
    let errorBody: unknown = null;
    const isJson = res.headers.get("content-type")?.includes("application/json");
    try {
      errorBody = isJson ? await res.json() : await res.text();
    } catch {}
    throw new ApiError(`HTTP ${res.status} on ${path}`, res.status, errorBody);
  }

  return {
    blob: await res.blob(),
    filename: parseContentDispositionFilename(res.headers.get("content-disposition")),
  };
}

// helpers
export const get = <T>(path: string, opts: Omit<HttpOptions, "method" | "body"> = {}) =>
  http<T>(path, { ...opts, method: "GET" });

export const post = <T>(
  path: string,
  body?: unknown,
  opts: Omit<HttpOptions, "method" | "body"> = {}
) => http<T>(path, { ...opts, method: "POST", body: body ?? {} });

export const put = <T>(
  path: string,
  body?: unknown,
  opts: Omit<HttpOptions, "method" | "body"> = {}
) => http<T>(path, { ...opts, method: "PUT", body: body ?? {} });

export const del = <T>(path: string, opts: Omit<HttpOptions, "method" | "body"> = {}) =>
  http<T>(path, { ...opts, method: "DELETE" });
